import { useMemo } from 'react';
import { Layout, Menu, Tooltip, Button } from 'antd';
import {
  SettingOutlined,
  RightOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import { useLocation } from 'react-router-dom';

import { usePanelNavigate } from '../../hooks/usePanelNavigate';
import classNames from 'classnames';

import Logo from '../common/Logo';
import WorkspaceSwitcher from './WorkspaceSwitcher';
import { ROUTES } from '../../constants/routes';
import { useAuth } from '../../contexts/AuthContext';
import { isMenuItemVisible, getDefaultPostLoginRoute } from '../../config/panelMenuAccess';
import { usesOrganizationPermissions } from '../../utils/orgPermissionAccess';
import { isPlatformSuperAdmin, requiresActiveOrganization } from '../../utils/authAccess';
import { attachPanelMenuLinks } from '../../utils/panelMenuLinks';
import {
  ACCOUNT_MENU_DEFS,
  MAIN_MENU_DEFS,
  SUPER_ADMIN_PRIMARY_MENU_DEFS,
  TENANT_PRIMARY_MENU_DEFS,
  collectPanelMenuRouteKeys,
  resolveSidebarSelectedKey,
} from '../../config/panelMenuDefs';

const { Sider } = Layout;

const buildVisibleItems = (defs, accessCtx) =>
  defs.filter((item) => isMenuItemVisible(item.key, accessCtx));

const resolveMenuKind = (user) => {
  if (isPlatformSuperAdmin(user)) return 'super-admin';
  if (usesOrganizationPermissions(user)) return 'tenant';
  return 'default';
};

const buildStructuredMainMenuChildren = (accessCtx, menuKind) => {
  if (menuKind === 'super-admin') return buildVisibleItems(SUPER_ADMIN_PRIMARY_MENU_DEFS, accessCtx);
  if (menuKind === 'tenant') return buildVisibleItems(TENANT_PRIMARY_MENU_DEFS, accessCtx);
  const items = buildVisibleItems(MAIN_MENU_DEFS, accessCtx);
  if (isMenuItemVisible(ROUTES.WEBHOOK_DEAD_LETTERS, accessCtx)) {
    items.push({ key: ROUTES.WEBHOOK_DEAD_LETTERS, icon: <SettingOutlined />, label: 'صف وب‌هوک' });
  }
  return items;
};

const buildAccountMenuChildren = (accessCtx) => buildVisibleItems(ACCOUNT_MENU_DEFS, accessCtx);

const buildMainMenuItems = (accessCtx, menuKind = 'default', { collapsed = false } = {}) => {
  const children = buildStructuredMainMenuChildren(accessCtx, menuKind);
  if (collapsed) return children;
  return [
    {
      type: 'group',
      label: 'منوی اصلی',
      key: 'main-group',
      children,
    },
  ];
};

const buildAccountMenuItems = (accessCtx, { collapsed = false } = {}) => {
  const children = buildAccountMenuChildren(accessCtx);
  if (!children.length) return [];
  if (collapsed) return children;
  return [
    {
      type: 'group',
      label: 'حساب کاربری',
      key: 'account-group',
      children,
    },
  ];
};

/**
 * نوار کناری اصلی پنل - شامل هدر زیبا با Logo و Workspace switcher.
 * طراحی رسپانسیو: در حالت collapsed آیتم‌ها فشرده، در موبایل به صورت Drawer باز می‌شود.
 */
const Sidebar = ({ collapsed, onToggle, isMobile = false }) => {
  const navigate = usePanelNavigate();
  const location = useLocation();
  const { user, organizations, activeOrganization } = useAuth();
  const accessCtx = useMemo(
    () => ({ user, activeOrganization, organizations }),
    [user, activeOrganization, organizations],
  );
  const showOrgSwitcher = requiresActiveOrganization(user);
  const platformSuperAdmin = isPlatformSuperAdmin(user);
  const menuKind = resolveMenuKind(user);

  const mainMenuItems = useMemo(
    () => attachPanelMenuLinks(buildMainMenuItems(accessCtx, menuKind, { collapsed })),
    [collapsed, accessCtx, menuKind],
  );

  const accountMenuItems = useMemo(
    () => attachPanelMenuLinks(buildAccountMenuItems(accessCtx, { collapsed })),
    [collapsed, accessCtx],
  );

  const allKeys = useMemo(
    () => collectPanelMenuRouteKeys([
      ROUTES.AGENDA_TEMPLATES,
      ROUTES.PLATFORM_ADMINS,
      ROUTES.SYSTEM_ORGANIZATION_ROLES,
      ROUTES.WEBHOOK_DEAD_LETTERS,
    ]),
    [],
  );

  const selectedKeys = useMemo(() => {
    const path = location.pathname;
    const workspacePath = path === ROUTES.WORKSPACE || path.startsWith(`${ROUTES.WORKSPACE}/`);
    if (workspacePath) return [ROUTES.WORKSPACE];
    return resolveSidebarSelectedKey(path, allKeys, getDefaultPostLoginRoute(accessCtx));
  }, [location.pathname, allKeys, accessCtx]);

  const handleSelect = ({ key, domEvent }) => {
    if (typeof key === 'string' && !domEvent?.target?.closest('a')) {
      if (domEvent?.metaKey || domEvent?.ctrlKey || domEvent?.shiftKey) {
        window.open(key, '_blank', 'noopener,noreferrer');
      } else {
        navigate(key);
      }
    }
    if (isMobile) onToggle?.();
  };

  return (
    <Sider
      collapsed={collapsed}
      collapsible
      trigger={null}
      width={272}
      collapsedWidth={isMobile ? 0 : 84}
      breakpoint="lg"
      className={classNames(
        'app-sidebar',
        'relative backdrop-blur-xl',
        'border-l-2 border-[var(--color-border)]',
        'shadow-[-12px_0_32px_-14px_rgba(7,11,20,0.1)] dark:shadow-[-12px_0_40px_-12px_rgba(0,0,0,0.5)]',
        'transition-all duration-300',
      )}
      style={{
        position: 'sticky',
        top: 0,
        height: '100vh',
        overflow: 'hidden',
      }}
    >
      <div className="flex h-full flex-col">
        {/* ══════════ هدر زیبای سایدبار ══════════ */}
        <div className="relative overflow-hidden border-b-2 border-slate-200/80 dark:border-slate-600/40">
          {/* لایه گرادیانی پس‌زمینه ظریف */}
          <div className="app-sidebar-header-glow pointer-events-none absolute inset-0 opacity-[0.06] dark:opacity-[0.08]" />

          <div
            dir={collapsed ? undefined : 'ltr'}
            className={classNames(
              'relative flex items-center px-4 pt-5 pb-4',
              collapsed ? 'justify-center' : 'justify-between gap-2',
            )}
          >
            <Logo collapsed={collapsed} size="md" className={collapsed ? undefined : 'min-w-0 flex-1'} />
            {!isMobile && (
              <Tooltip title={collapsed ? 'بازکردن منو' : 'جمع‌کردن منو'} placement="left">
                <Button
                  type="text"
                  shape="circle"
                  size="small"
                  onClick={onToggle}
                  icon={<RightOutlined style={{ fontSize: 11 }} className={collapsed ? 'rotate-180' : ''} />}
                  className={classNames(
                    '!h-7 !w-7 !text-slate-400 hover:!bg-slate-100 hover:!text-brand-600 dark:hover:!bg-white/10 dark:hover:!text-white',
                    collapsed && 'absolute left-2 top-3 z-10 !bg-[var(--color-surface)] !shadow-sm',
                  )}
                />
              </Tooltip>
            )}
          </div>

          {showOrgSwitcher && (!collapsed || !isMobile) ? (
            <WorkspaceSwitcher collapsed={collapsed} isMobile={isMobile} onToggle={onToggle} />
          ) : null}
          {!showOrgSwitcher && platformSuperAdmin && (
            <div className={classNames('relative px-3 pb-4', collapsed && 'flex justify-center')}>
              <Tooltip title="مدیریت کل سیستم" placement={collapsed ? 'left' : 'bottom'}>
                <div
                  className={classNames(
                    'flex items-center gap-3 rounded-xl border border-brand-200/80 bg-brand-50/60 px-3 py-2.5',
                    'dark:border-brand-500/25 dark:bg-brand-500/10',
                    collapsed && 'h-11 w-11 justify-center px-0',
                  )}
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-gradient text-white shadow-soft">
                    <SafetyCertificateOutlined className="text-base" />
                  </span>
                  {!collapsed ? (
                    <div className="flex min-w-0 flex-1 flex-col items-start text-right">
                      <span className="truncate text-sm font-semibold text-brand-800 dark:text-brand-200">
                        مدیریت کل سیستم
                      </span>
                      <span className="truncate text-[11px] text-brand-600/80 dark:text-brand-300/80">
                        سوپرادمین پلتفرم
                      </span>
                    </div>
                  ) : null}
                </div>
              </Tooltip>
            </div>
          )}
        </div>

        {/* ══════════ منوی اصلی (اسکرول) + حساب کاربری ثابت ══════════ */}
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 overflow-y-auto py-2">
            <Menu
              mode="inline"
              inlineCollapsed={collapsed}
              selectedKeys={selectedKeys}
              items={mainMenuItems}
              onClick={handleSelect}
              className="!border-0 !bg-transparent"
              style={{ background: 'transparent' }}
            />
          </div>

          {accountMenuItems.length > 0 && (
            <div className="app-sidebar-account shrink-0">
              <Menu
                mode="inline"
                inlineCollapsed={collapsed}
                selectedKeys={selectedKeys}
                items={accountMenuItems}
                onClick={handleSelect}
                className="!border-0 !bg-transparent"
                style={{ background: 'transparent' }}
              />
            </div>
          )}
        </div>

        {collapsed && !isMobile && (
          <div className="shrink-0 border-t border-slate-200/70 p-3 dark:border-white/10">
            <Tooltip title="بازکردن منو" placement="left">
              <Button
                block
                type="text"
                onClick={onToggle}
                icon={<RightOutlined className="flip-rtl" />}
                className="!h-9 !text-slate-400 hover:!bg-slate-100 hover:!text-brand-600 dark:hover:!bg-white/10 dark:hover:!text-white"
              />
            </Tooltip>
          </div>
        )}
      </div>
    </Sider>
  );
};

export default Sidebar;
